/**
 * Work Streams -- every demand's path from "somebody wants something" to "we are building it".
 *
 * ED, 2026-10-28: "Build the Work Streams page", from the source product's screen. A stream is NOT
 * a record. It is a quote request drawn with what hangs off it: the offers drafted against it and
 * the plans drafted from those, read in one call (List Quote Requests with IncludeChain). The step
 * a stream is on follows from the chain -- Quote Request until an offer answers it, Quote Response
 * until a plan is drafted from the offer, then Build Plan -- exactly as the source product derives
 * it, and for the same reason: a step that was a stored field could disagree with the records.
 *
 * DEMAND IS THE REQUEST ITSELF, READ AS A DEMAND. The source product's "Add demand" makes a project
 * named with the sentence and a quote request in it; ours does the same through the doors that
 * exist -- Create Project, Create Quote Request, Add To Project -- so a demand added here is a
 * request filed in a folder, which is what the rest of the product already knows how to show.
 *
 * ADVANCE CREATES THE NEXT RECORD. Quote Request -> Quote Response makes an offer through the
 * offer's own door, answering this request and filed in its project; Quote Response -> Build Plan
 * drafts a plan from the offer's revision for the demand's quantity. Nothing here is an agent: the
 * source product's "Analyze & Prepare Quote" and "Prepare with AI assistant" are its fenced
 * drafting layer and are not imitated.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        MenuItem, TextField, Typography} from "@mui/material";
import {ArrowForward, CheckCircleOutlined, RadioButtonUnchecked} from "@mui/icons-material";
import RecordDrawer from "../components/Foundation/RecordDrawer";
import ObjectIdLink, {RecordDrawerContext} from "../components/Project/ObjectIdLink";

import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import {AppContext} from "../util/AppContext";
import {makeCresAxios} from "../util/Axios";
import {hasPrivilege, useSessionContext} from "../util/SessionContext";
import {statusPill} from "../util/statusPill";
import {Blue3, Grey2, Grey5, Grey6, Grey7, Grey8, Grey17, White} from "../theme/styles";

type Plan = {BuildPlanRevisionUSID?: string | null; ObjectUSID?: string | null; ObjectID?: string | null;
             Name?: string | null; Status?: string | null; Quantity?: string | null};
type Offer = {PreparedQuoteRevisionUSID: string; ObjectUSID?: string | null; ObjectID?: string | null;
              Name?: string | null; Status?: string | null; Currency?: string | null;
              ItemObjectUSID?: string | null; ListOfBuildPlan?: {BuildPlan?: Plan[] | Plan}};
type Request = {USID: string; ObjectUSID?: string | null; ObjectID?: string | null;
                ObjectRevisionUSID?: string | null; RequestNumber?: string | null;
                Subject?: string | null; RequestedProduct?: string | null; ExtractedProductName?: string | null;
                RequesterName?: string | null; RequesterEmail?: string | null;
                CustomerObjectUSID?: string | null; ItemObjectUSID?: string | null;
                Quantity?: string | null; UnitOfMeasure?: string | null; Status?: string | null;
                ReceivedDatetime?: string | null; CreatedDatetime?: string | null;
                ListOfPreparedQuote?: {PreparedQuote?: Offer[] | Offer};
                Project?: {USID: string; Name?: string | null; ObjectID?: string | null} | null};

const listOf = <T,>(held: T[] | T | undefined | null): T[] =>
    Array.isArray(held) ? held : (held ? [held] : []);

type Step = 1 | 2 | 3;
const STEPS: {index: Step; label: string; actor: string; blurb: string}[] = [
    {index: 1, label: "Quote Request", actor: "User", blurb: "The demand, as a request to quote."},
    {index: 2, label: "Quote Response", actor: "User", blurb: "An offer drafted against the request: lines, terms, the three questions."},
    {index: 3, label: "Build Plan", actor: "System + User", blurb: "A plan drafted from the offer for the demand's quantity, with a date."},
];

/** The stream the chain amounts to. First offer wins, then its first plan, as the source does. */
function derive(request: Request) {
    const offers = listOf<Offer>(request.ListOfPreparedQuote?.PreparedQuote);
    const offer = offers[0] ?? null;
    const plans = offer ? listOf<Plan>(offer.ListOfBuildPlan?.BuildPlan) : [];
    const plan = plans[0] ?? null;
    const step: Step = plan ? 3 : offer ? 2 : 1;
    const product = request.RequestedProduct || request.ExtractedProductName || request.Subject || "Unnamed demand";
    const customer = request.RequesterName || request.RequesterEmail || "Unknown";
    return {offers, offer, plans, plan, step, product, customer};
}

function quantityOf(request: Request): string {
    const amount = request.Quantity ? Number(request.Quantity) : null;
    if (amount == null || !Number.isFinite(amount)) return "—";
    return `${amount.toLocaleString()} ${request.UnitOfMeasure ?? "each"}`;
}

export default function WorkStreams() {
    const appContext: any = useContext(AppContext);
    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext), [appContext?.sessionContext]);
    // THE RECORD DRAWER, one per page (Ed, 2026-10-30): every identifier on this page opens it.
    const [drawer, setDrawer] = useState<string | null>(null);

    const [requests, setRequests] = useState<Request[] | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [advancing, setAdvancing] = useState<null | {stream: Request; to: Step}>(null);

    const load = useCallback(() => {
        api.get("t1/v1/quote_requests", {params: {IncludeChain: "true", IncludeClosed: "true"},
                                         headers: {"idoc-response": "v1.QuoteRequest-001"}})
            .then((r: any) => {
                const rows = listOf<Request>(r?.data?.PlatformMessage?.ListOfQuoteRequest?.QuoteRequest);
                setRequests(rows);
                setProblem(null);
            })
            .catch((failure: any) => {
                setRequests([]);
                setProblem(failure?.response?.data?.Cause ?? "The work streams could not be read.");
            });
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const streams = useMemo(() => (requests ?? []).map(one => ({request: one, ...derive(one)})), [requests]);
    useEffect(() => {
        if (selected && streams.some(one => one.request.USID === selected)) return;
        setSelected(streams[0]?.request.USID ?? null);
    }, [streams, selected]);
    const current = streams.find(one => one.request.USID === selected) ?? null;
    const counts = useMemo(() => ({
        1: streams.filter(one => one.step === 1).length,
        2: streams.filter(one => one.step === 2).length,
        3: streams.filter(one => one.step === 3).length,
    }), [streams]);

    const mayAdd = hasPrivilege("project:create") || hasPrivilege("project:edit");

    return (
        <RecordDrawerContext.Provider value={setDrawer}>
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%", backgroundColor: White, minHeight: 0}}>
            <PageHeader title="Work Streams"><PersonaHeaderInfo/></PageHeader>
            <div style={{display: "flex", flexDirection: "row", flexGrow: 1, minHeight: 0}}>
                {/* THE RAIL: every stream, with the step it is on. */}
                <div style={{width: 300, flexShrink: 0, borderRight: `1px solid ${Grey2}`, overflow: "auto",
                             display: "flex", flexDirection: "column"}}>
                    <div style={{padding: "14px 16px 8px", display: "flex", alignItems: "baseline", gap: 8}}>
                        <Typography sx={{fontSize: 11, fontWeight: 700, color: Grey6, letterSpacing: "0.06em"}}>ACTIVE STREAMS</Typography>
                        <Box sx={{flex: 1}}/>
                        <Typography sx={{fontSize: 12, color: Grey6}}>{streams.length}</Typography>
                    </div>
                    {requests === null && !problem && (
                        <Box sx={{display: "flex", gap: "8px", alignItems: "center", padding: "8px 16px"}}>
                            <CircularProgress size={14}/>
                            <Typography sx={{fontSize: 12.5, color: Grey7}}>Reading the streams…</Typography>
                        </Box>
                    )}
                    {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E", padding: "8px 16px"}}>{problem}</Typography>}
                    {requests && streams.length === 0 && (
                        <Typography sx={{fontSize: 12.5, color: Grey6, padding: "8px 16px", lineHeight: 1.5}}>
                            No streams yet. A stream begins with a demand: add one, or file a quote request in a project.
                        </Typography>
                    )}
                    {streams.map(one => (
                        <StreamCard key={one.request.USID} stream={one}
                                    active={one.request.USID === selected}
                                    onClick={() => setSelected(one.request.USID)}/>
                    ))}
                </div>

                {/* THE STREAM */}
                <div style={{flex: 1, overflow: "auto", padding: "0 30px", display: "flex", flexDirection: "column", minHeight: 0}}>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${Grey2}`}}>
                        <div style={{display: "flex", alignItems: "baseline", gap: 12}}>
                            <Typography sx={{fontSize: 20, fontWeight: 500, color: Grey17}}>Work Streams</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey6}}>{streams.length} stream{streams.length === 1 ? "" : "s"} · quote → build</Typography>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            {STEPS.map(step => (
                                <Chip key={step.index} size="small" label={`${step.label}  ${counts[step.index]}`}
                                      sx={{fontSize: 11.5, height: 22, background: "#F2F6FA", color: Grey8}}/>
                            ))}
                            {mayAdd && (
                                <Button size="small" variant="contained" disableElevation sx={{textTransform: "none", marginLeft: "8px"}}
                                        onClick={() => setAdding(true)}>+ Add demand</Button>
                            )}
                        </div>
                    </div>

                    {current ? (
                        <StreamDetail stream={current} api={api}
                                      onAdvance={to => setAdvancing({stream: current.request, to})}/>
                    ) : (
                        requests && <Typography sx={{fontSize: 13, color: Grey6, padding: "24px 0"}}>Pick a stream on the left.</Typography>
                    )}
                </div>
            </div>

            <RecordDrawer revisionUSID={drawer} sessionContext={sessionContext} onClose={() => setDrawer(null)}/>
            {adding && <AddDemandDialog api={api} sessionContext={sessionContext}
                                        onClose={() => setAdding(false)}
                                        onAdded={usid => { setAdding(false); setSelected(usid); load(); }}/>}
            {advancing && <AdvanceDialog api={api} sessionContext={sessionContext}
                                         stream={advancing.stream} to={advancing.to}
                                         onClose={() => setAdvancing(null)}
                                         onDone={() => { setAdvancing(null); load(); }}/>}
        </div>
        </RecordDrawerContext.Provider>
    );
}

function StreamCard({stream, active, onClick}: {stream: ReturnType<typeof derive> & {request: Request}; active: boolean; onClick: () => void}) {
    const {request, step, product, customer} = stream;
    return (
        <Box onClick={onClick}
             sx={{margin: "0 10px 8px", padding: "10px 12px", border: `1px solid ${active ? Blue3 : Grey2}`,
                  borderRadius: "8px", cursor: "pointer", background: active ? "#F3F9FD" : White}}>
            <Box sx={{display: "flex", alignItems: "flex-start", gap: "8px"}}>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17, flex: 1, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={product}>{product}</Typography>
                {statusPill(request.Status ?? null)}
            </Box>
            <Typography sx={{fontSize: 11.5, color: Grey7}}>{customer}</Typography>
            <Box sx={{display: "flex", alignItems: "center", gap: "4px", marginTop: "6px"}}>
                {STEPS.map(one => (
                    <React.Fragment key={one.index}>
                        <Box sx={{width: 8, height: 8, borderRadius: 4,
                                  background: one.index < step ? "#1E9E5A" : one.index === step ? Blue3 : "#D5DBE3"}}/>
                        {one.index < 3 && <Box sx={{flex: 1, height: 1, background: "#D5DBE3"}}/>}
                    </React.Fragment>
                ))}
            </Box>
            <Box sx={{display: "flex", alignItems: "baseline", marginTop: "4px"}}>
                <Typography sx={{fontSize: 11, color: step === 3 ? "#B26A00" : Blue3}}>{STEPS[step - 1].label}</Typography>
                <Box sx={{flex: 1}}/>
                <Typography sx={{fontSize: 11, color: Grey6}}>{quantityOf(request)}</Typography>
            </Box>
        </Box>
    );
}

function StreamDetail({stream, api, onAdvance}: {
    stream: ReturnType<typeof derive> & {request: Request};
    api: any;
    onAdvance: (to: Step) => void;
}) {
    const {request, offer, plan, step, product, customer} = stream;
    // THE PROJECT IS NAMED BY ITS LOGICAL OBJECT in the chain, and the drawer and the record route
    // both want a revision: ask the project's door for the current one when the identifier is
    // clicked. Until 2026-10-30 this link took the object's key to a route that names a revision,
    // and so went nowhere -- which is what Ed saw.
    const projectRevision = async () => {
        const answer = await api.get("t1/v1/projects", {params: {ObjectUSID: request.Project!.USID},
                                                        headers: {"idoc-response": "v1.ProjectRevision-001"}});
        const held = answer?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision;
        const rows = held == null ? [] : (Array.isArray(held) ? held : [held]);
        return rows[0]?.USID ?? null;
    };
    const mayEdit = hasPrivilege("project:edit");
    return (
        <Box sx={{padding: "16px 0 30px", display: "flex", flexDirection: "column", gap: "14px"}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap"}}>
                <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17}}>{product}</Typography>
                <Typography sx={{fontSize: 12.5, color: Grey7}}>{customer}</Typography>
                {statusPill(request.Status ?? null)}
                <Typography sx={{fontSize: 12.5, color: Blue3}}>Step {step}: {STEPS[step - 1].label}</Typography>
                {request.Project && (
                    <Typography sx={{fontSize: 12.5, color: Grey7}}>
                        · filed in <ObjectIdLink id={request.Project.ObjectID ?? request.Project.Name} resolveRevision={projectRevision}
                                                 size={12.5} weight={500}/>
                    </Typography>
                )}
            </Box>

            {/* THE STEP STRIP */}
            <Box sx={{display: "flex", gap: "2px"}}>
                {STEPS.map(one => {
                    const done = one.index < step, now = one.index === step;
                    return (
                        <Box key={one.index} sx={{flex: 1, padding: "10px 12px", borderRadius: "6px",
                                                  background: now ? Blue3 : done ? "#EEF8F1" : "#F7F9FC",
                                                  color: now ? White : done ? "#1E9E5A" : Grey7}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: "6px"}}>
                                {done ? <CheckCircleOutlined sx={{fontSize: 16}}/> : <RadioButtonUnchecked sx={{fontSize: 16}}/>}
                                <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase"}}>{one.label}</Typography>
                            </Box>
                            <Typography sx={{fontSize: 11, opacity: 0.85, paddingTop: "2px"}}>{one.actor}</Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* DEMAND */}
            <Panel title="Demand" state="done" actor="User">
                <Typography sx={{fontSize: 12.5, color: Grey7, paddingBottom: "8px"}}>What was asked for, as the request records it.</Typography>
                <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px"}}>
                    <Fact label="Product" value={product}/>
                    <Fact label="Customer" value={customer}/>
                    <Fact label="Quantity" value={quantityOf(request)}/>
                    <Fact label="Received" value={request.ReceivedDatetime ? new Date(request.ReceivedDatetime).toLocaleDateString() : "—"}/>
                </Box>
            </Panel>

            {/* QUOTE REQUEST */}
            {/* THE NUMBER IS THE DOOR (Ed, 2026-10-30): the drawer on the number, a new window on
                the icon beside it, and no "Open the record" at the right any more. */}
            <Panel title="Quote Request" state={step > 1 ? "done" : "now"} actor="User">
                <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px"}}>
                    <Fact label="Number" value={<ObjectIdLink id={request.ObjectID ?? request.RequestNumber} revisionUSID={request.ObjectRevisionUSID} weight={500}/>}/>
                    <Fact label="Status" value={request.Status ?? "—"}/>
                </Box>
                {step === 1 && mayEdit && (
                    <Button fullWidth variant="contained" disableElevation sx={{textTransform: "none", marginTop: "12px"}}
                            endIcon={<ArrowForward/>} onClick={() => onAdvance(2)}>
                        Advance to Quote Response
                    </Button>
                )}
            </Panel>

            {/* QUOTE RESPONSE */}
            <Panel title="Quote Response" state={step > 2 ? "done" : step === 2 ? "now" : "later"} actor="User">
                {offer ? (
                    <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px"}}>
                        <Fact label="Number" value={<ObjectIdLink id={offer.ObjectID} revisionUSID={offer.PreparedQuoteRevisionUSID} weight={500}/>}/>
                        <Fact label="Name" value={offer.Name ?? "—"}/>
                        <Fact label="Status" value={offer.Status ?? "—"}/>
                        <Fact label="Currency" value={offer.Currency ?? "—"}/>
                    </Box>
                ) : (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>No offer has been drafted against this request yet.</Typography>
                )}
                {stream.offers.length > 1 && (
                    <Typography sx={{fontSize: 11.5, color: Grey6, paddingTop: "6px"}}>
                        {stream.offers.length} offers answer this request; the stream follows the first.
                    </Typography>
                )}
                {step === 2 && mayEdit && (
                    <Button fullWidth variant="contained" disableElevation sx={{textTransform: "none", marginTop: "12px"}}
                            endIcon={<ArrowForward/>} onClick={() => onAdvance(3)}>
                        Advance to Build Plan
                    </Button>
                )}
            </Panel>

            {/* BUILD PLAN */}
            <Panel title="Build Plan" state={step === 3 ? "now" : "later"} actor="System + User">
                {plan ? (
                    <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px"}}>
                        <Fact label="Number" value={<ObjectIdLink id={plan.ObjectID} revisionUSID={plan.BuildPlanRevisionUSID} weight={500}/>}/>
                        <Fact label="Name" value={plan.Name ?? "—"}/>
                        <Fact label="Status" value={plan.Status ?? "—"}/>
                        <Fact label="Quantity" value={plan.Quantity ?? "—"}/>
                    </Box>
                ) : (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        No build plan linked yet. {step === 2 ? "Advance the quote response to draft one from the offer." : "One is drafted from an offer."}
                    </Typography>
                )}
            </Panel>
        </Box>
    );
}

function Panel({title, state, actor, action, children}: {
    title: string; state: "done" | "now" | "later"; actor: string; action?: React.ReactNode; children: React.ReactNode;
}) {
    return (
        <Box sx={{border: `1px solid ${state === "now" ? Blue3 : Grey2}`, borderRadius: "8px", padding: "12px 16px",
                  background: state === "now" ? "#F7FBFE" : White}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "10px", paddingBottom: "6px"}}>
                {state === "done" ? <CheckCircleOutlined sx={{fontSize: 18, color: "#1E9E5A"}}/>
                    : <RadioButtonUnchecked sx={{fontSize: 18, color: state === "now" ? Blue3 : Grey5}}/>}
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, flex: 1}}>{title}</Typography>
                <Chip size="small" label={actor} sx={{fontSize: 10.5, height: 18, background: "#EEF1F5", color: Grey7}}/>
                {state === "done" && <Chip size="small" label="Done" sx={{fontSize: 10.5, height: 18, background: "#EEF8F1", color: "#1E9E5A"}}/>}
                {state === "now" && <Chip size="small" label="In Progress" sx={{fontSize: 10.5, height: 18, background: "#E8F4FA", color: Blue3}}/>}
                {action}
            </Box>
            {children}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 10.5, color: Grey6, textTransform: "uppercase", letterSpacing: "0.05em"}}>{label}</Typography>
            {typeof value === "string"
                ? <Typography sx={{fontSize: 13, color: Grey17}}>{value}</Typography>
                : <Box sx={{display: "flex", alignItems: "center", minHeight: 20}}>{value}</Box>}
        </Box>
    );
}

/** The one object type a species offers, or the first active one; a dialog has to name one. */
async function objectTypeFor(api: any, classification: string): Promise<string | null> {
    const r = await api.get("t1/v1/object_types", {params: {ObjectClassification: classification, Active: "true", Limit: 50}});
    const list = r?.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
    const rows = Array.isArray(list) ? list : [list];
    return rows[0]?.USID ?? null;
}

/**
 * ADD DEMAND: a project named with the sentence, a quote request in it, filed. Three doors the
 * product already has, in the order the project view uses them; a failure after the first says
 * what exists so nothing is lost.
 */
function AddDemandDialog({api, sessionContext, onClose, onAdded}: {
    api: any; sessionContext: any; onClose: () => void; onAdded: (requestUSID: string) => void;
}) {
    const [sentence, setSentence] = useState("");
    const [customer, setCustomer] = useState("");
    const [quantity, setQuantity] = useState("1");
    const [unit, setUnit] = useState("EA");
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const units: string[] = useMemo(() => {
        try {
            return (sessionContext?.getPickValues?.() ?? []).filter((one: any) => one?.Type === "Unit of Measure")
                .map((one: any) => String(one.Value)).sort();
        } catch { return []; }
    }, [sessionContext]);

    const add = async () => {
        const title = sentence.trim();
        if (!title) return;
        setBusy(true); setProblem(null);
        try {
            const project = await api.post("t1/v1/projects", {Name: title, Description: `Demand: ${title}`});
            // THE SPECIES DOOR ANSWERS A LIST OF ONE REVISION; the folder's key is its logical object.
            const made0 = listOf<any>(project?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision)[0];
            const projectUSID = made0?.ObjectUSID ?? project?.data?.PlatformMessage?.Project?.ObjectUSID ?? null;
            if (!projectUSID) throw new Error("The project was created but came back without an identifier.");
            const made = await api.post("t1/v1/quote_requests",
                {Subject: title, RequestedProduct: title, RequesterName: customer.trim() || undefined,
                 Quantity: quantity.trim() || undefined, UnitOfMeasure: unit || undefined},
                {headers: {"idoc-response": "v1.QuoteRequest-001"}});
            const request = made?.data?.PlatformMessage?.QuoteRequest;
            if (!request?.ObjectUSID) throw new Error("The request was created but came back without an identifier.");
            await api.post("t1/v1/project_members", {USID: projectUSID, MemberType: "QuoteRequest", MemberUSID: request.ObjectUSID});
            onAdded(request.USID);
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? failure?.message ?? "The demand could not be added.");
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>Add demand</DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 12.5, color: Grey7, paddingBottom: "12px", lineHeight: 1.5}}>
                    One sentence for what is wanted. It becomes a project named with the sentence and a quote
                    request filed in it, which is where the stream begins.
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                    <TextField label="What is wanted" size="small" autoFocus value={sentence}
                               onChange={e => setSentence(e.target.value)} placeholder="1000 catheters for the stent line"/>
                    <TextField label="Customer" size="small" value={customer} onChange={e => setCustomer(e.target.value)}
                               helperText="Who is asking. Optional; a customer record can be linked on the request later."/>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Quantity" size="small" value={quantity} onChange={e => setQuantity(e.target.value)} sx={{flex: 1}}/>
                        <TextField label="Unit" size="small" select value={unit} onChange={e => setUnit(e.target.value)} sx={{flex: 1}}>
                            {(units.length ? units : ["EA"]).map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                        </TextField>
                    </Box>
                    {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{problem}</Typography>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={busy || !sentence.trim()}
                        onClick={add} sx={{textTransform: "none"}}>{busy ? "Adding…" : "Add demand"}</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * ADVANCE: the next record, through its own door, answering the one before it.
 *   to 2: an offer (Create Prepared Quote with QuoteRequestUSID and a currency), filed in the
 *         request's project.
 *   to 3: a plan (Create Build Plan with PreparedQuoteRevisionUSID, the demand's quantity and the
 *         offer's item), filed in the same project.
 */
function AdvanceDialog({api, sessionContext, stream, to, onClose, onDone}: {
    api: any; sessionContext: any; stream: Request; to: Step; onClose: () => void; onDone: () => void;
}) {
    const {offer, product} = derive(stream);
    const [name, setName] = useState(to === 2 ? `Offer: ${product}` : `Build ${stream.Quantity ?? ""} ${product}`.replace(/\s+/g, " ").trim());
    const [currency, setCurrency] = useState(offer?.Currency ?? "USD");
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const currencies: string[] = useMemo(() => {
        try {
            return (sessionContext?.getPickValues?.() ?? []).filter((one: any) => one?.Type === "Currency")
                .map((one: any) => String(one.Value)).sort();
        } catch { return []; }
    }, [sessionContext]);

    const go = async () => {
        setBusy(true); setProblem(null);
        try {
            // THE OWNER, NAMED. The species doors take the revision as sent and do not default an
            // owner from the persona -- the generic create dialog names one, and the project door
            // resolves one itself -- so a plan advanced here without this was created with no
            // owner and visible to nobody (BP-00002, 2026-10-28). A Position persona is the owner;
            // the widening personas are a set of positions, not an owner, so they are refused.
            const active = sessionContext?.getActiveVisibility?.();
            if (!active || String(active.Mode) !== "Position" || !active.Value) {
                throw new Error("Open this under a Position persona: the record it creates needs a position to belong to.");
            }
            const owner = {PrimaryPositionUSID: String(active.Value)};
            let objectUSID: string | null = null;
            let kind = "";
            if (to === 2) {
                kind = "PreparedQuote";
                const type = await objectTypeFor(api, "PreparedQuote");
                if (!type) throw new Error("No active quote response type is configured.");
                const idoc = "v1.PreparedQuoteRevision-001";
                const r = await api.post("t1/v1/prepared_quotes",
                    {PlatformMessage: {"-idoc": idoc, PreparedQuoteRevision: {Name: name.trim() || `Offer: ${product}`, AssignObjectTypeUSID: type, ...owner}},
                     Currency: currency, QuoteRequestUSID: stream.USID,
                     CustomerObjectUSID: stream.CustomerObjectUSID ?? undefined},
                    {headers: {"idoc-request": idoc, "idoc-response": idoc}});
                const rows = listOf<any>(r?.data?.PlatformMessage?.ListOfPreparedQuoteRevision?.PreparedQuoteRevision);
                objectUSID = rows[0]?.ObjectUSID ?? null;
            } else {
                kind = "BuildPlan";
                if (!offer) throw new Error("There is no offer to draft a plan from.");
                const type = await objectTypeFor(api, "BuildPlan");
                if (!type) throw new Error("No active build plan type is configured.");
                const idoc = "v1.BuildPlanRevision-001";
                const r = await api.post("t1/v1/build_plans",
                    {PlatformMessage: {"-idoc": idoc, BuildPlanRevision: {Name: name.trim() || `Build ${product}`, AssignObjectTypeUSID: type, ...owner}},
                     Quantity: stream.Quantity ?? "1", UnitOfMeasure: stream.UnitOfMeasure ?? undefined,
                     PreparedQuoteRevisionUSID: offer.PreparedQuoteRevisionUSID,
                     ItemObjectUSID: offer.ItemObjectUSID ?? stream.ItemObjectUSID ?? undefined,
                     Currency: offer.Currency ?? currency},
                    {headers: {"idoc-request": idoc, "idoc-response": idoc}});
                const rows = listOf<any>(r?.data?.PlatformMessage?.ListOfBuildPlanRevision?.BuildPlanRevision);
                objectUSID = rows[0]?.ObjectUSID ?? null;
            }
            // FILED WHERE THE REQUEST IS. A record made from a stream belongs in the stream's folder;
            // without one it still exists and the chain still finds it.
            if (objectUSID && stream.Project?.USID) {
                await api.post("t1/v1/project_members", {USID: stream.Project.USID, MemberType: kind, MemberUSID: objectUSID})
                    .catch(() => null);
            }
            onDone();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? failure?.message ?? "The step could not be advanced.");
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>{to === 2 ? "Advance to Quote Response" : "Advance to Build Plan"}</DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 12.5, color: Grey7, paddingBottom: "12px", lineHeight: 1.5}}>
                    {to === 2
                        ? "Drafts an offer answering this request, in the currency below, filed in the request's project. Its lines and terms are set on the offer."
                        : `Drafts a build plan from the offer for ${stream.Quantity ?? "the demand's quantity"} ${stream.UnitOfMeasure ?? ""}, filed in the request's project.`}
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                    <TextField label="Name" size="small" value={name} onChange={e => setName(e.target.value)}/>
                    {to === 2 && (
                        <TextField label="Currency" size="small" select value={currency} onChange={e => setCurrency(e.target.value)}>
                            {(currencies.length ? currencies : ["USD"]).map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                        </TextField>
                    )}
                    {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{problem}</Typography>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={busy} onClick={go}
                        sx={{textTransform: "none"}}>{busy ? "Working…" : "Advance"}</Button>
            </DialogActions>
        </Dialog>
    );
}
